import './transactionList.css';
import React, {Component} from "react";
import {ExpenseDto, PaymentDto, UserDto, BasicUserDto} from "../model/functionModels";
import UserDisplay from "./userDisplay";
import Pagination from "./pagination";

interface TransactionListState {
    page: number,
    transactionsGrouped: TransactionPage[],
}

interface TransactionPage {
    key: string,
    dayGroups: TransactionDay[],
}

interface TransactionDay {
    key: string,
    transactions: (ExpenseDto|PaymentDto)[],
}

interface TransactionListProps {
    title: string,
    transactions: (ExpenseDto|PaymentDto)[],
    involvedUsers: (UserDto | BasicUserDto)[],
    showDetails: boolean,
    displayMode: DisplayMode,
    loggedInUserId?: string,
    onDeleteTransaction?: (transactionId: string) => Promise<void>,
}

export enum DisplayMode {
    PAYMENT = "PAYMENT", EXPENSE = "EXPENSE"
}

export class TransactionList extends Component<TransactionListProps, TransactionListState> {

    private static readonly OLD_CONNECTION_ICON_DATA_URL =
        "data:image/svg+xml;utf8," +
        encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
                <defs>
                    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stop-color="#4a9eff"/>
                        <stop offset="1" stop-color="#1e4b8f"/>
                    </linearGradient>
                </defs>
                <circle cx="40" cy="40" r="40" fill="url(#g)"/>
                <circle cx="40" cy="30" r="14" fill="rgba(255,255,255,0.35)"/>
                <path d="M16 70c6-14 18-22 24-22s18 8 24 22" fill="rgba(255,255,255,0.35)"/>
                <text x="40" y="76" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="rgba(255,255,255,0.9)">OLD</text>
            </svg>`
        );

    constructor(props: TransactionListProps) {
        super(props, {})
        this.getDayOfMonthForTransaction = this.getDayOfMonthForTransaction.bind(this);
        this.getUserDtoForUser = this.getUserDtoForUser.bind(this);
        let splitTransactions = this.splitTransactions(props.transactions);
        this.state = {
            page: splitTransactions.length -1,
            transactionsGrouped: splitTransactions
        };
    }


    componentDidUpdate(prevProps: Readonly<TransactionListProps>, prevState: Readonly<TransactionListState>, snapshot?: any) {
        if (prevProps !== this.props) {
            let splitTransactions = this.splitTransactions(this.props.transactions);
            this.setState({
                page: splitTransactions.length -1,
                transactionsGrouped: splitTransactions
            });
        }
    }

    getUserDtoForUser(id: string) {
        const userDto = this.props.involvedUsers.filter((user) => user.id === id)[0];
        if (!userDto) {
            // User not in current friends list (e.g. removed friend / deleted account).
            // Render as an anonymised placeholder to avoid leaking any data.
            return {
                id: id,
                username: "old-connection",
                firstName: "Old",
                lastName: "Connection",
                icon: "",
                gravatarIcon: TransactionList.OLD_CONNECTION_ICON_DATA_URL,
            } as BasicUserDto;
        }
        return userDto;
    }

    splitTransactions(transactions: (ExpenseDto|PaymentDto)[]) {
        let result: TransactionPage[] = [];
        // Sort transactions chronologically (newest first) before grouping
        const sortedTransactions = [...transactions].sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA; // Newest first
        });
        
        sortedTransactions.forEach((t : ExpenseDto | PaymentDto) => {
            const monthKey = this.getMonthKeyForTransaction(t);
            const dayKey = this.getDayKeyForTransaction(t);
            let foundIndex = result.findIndex((r) => r.key === monthKey);
            if (foundIndex >= 0) {
                let foundDayIndex = result[foundIndex].dayGroups.findIndex((d) => d.key === dayKey);
                if (foundDayIndex >= 0) {
                    result[foundIndex].dayGroups[foundDayIndex].transactions.push(t);
                } else {
                    result[foundIndex].dayGroups.push({
                        key: dayKey,
                        transactions: [t]
                    });
                    // Sort day groups within month (newest first)
                    result[foundIndex].dayGroups.sort((a, b) => {
                        const dateA = new Date(a.transactions[0].date).getTime();
                        const dateB = new Date(b.transactions[0].date).getTime();
                        return dateB - dateA;
                    });
                }
            } else {
                result.push({
                    key: monthKey,
                    dayGroups: [{
                        key: dayKey,
                        transactions: [t]
                    }],
                })
            }
        });
        
        // Sort pages chronologically (oldest first) by comparing month keys.
        // We default the selected page to the last index (most recent month).
        result.sort((a, b) => {
            const dateA = this.parseMonthKey(a.key);
            const dateB = this.parseMonthKey(b.key);
            return dateA.getTime() - dateB.getTime();
        });
        
        return result;
    }
    
    parseMonthKey(monthKey: string): Date {
        // monthKey format: "January 2024"
        const parts = monthKey.split(" ");
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const month = monthNames.indexOf(parts[0]);
        const year = parseInt(parts[1]);
        return new Date(year, month, 1);
    }

    getMonthKeyForTransaction(transaction: ExpenseDto | PaymentDto) {
        // transaction.date format: YYYY-MM-DD
        const dateParts = transaction.date.split("-");
        const year = dateParts[0]; // Use full year for proper sorting
        const month = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1).toLocaleString('default', { month: 'long' });

        return `${month} ${year}`;
    }

    getDayKeyForTransaction(transaction: ExpenseDto | PaymentDto) {
        let numeric = this.getDayOfMonthForTransaction(transaction);
        let terminator = "th";
        if (numeric%10 === 1) {
            terminator = "st";
        } else if (numeric%10 === 2) {
            terminator = "nd";
        } else if (numeric%10 === 3) {
            terminator = "rd";
        }

        let prefix = transaction.date.split("-")[2];
        if (numeric<10) {
            prefix = prefix.slice(-1);
        }
        return prefix+terminator;
    }

    getDayOfMonthForTransaction(transaction: ExpenseDto | PaymentDto) {
        const dateParts = transaction.date.split("-");
        return parseInt(dateParts[2]);
    }

    async componentDidMount() {
    }


    renderTransactions() {
        const tGroup = this.state.transactionsGrouped[this.state.page];
        return <div className="TransactionList-Group">
            {tGroup && tGroup.dayGroups && tGroup.dayGroups
                .sort((t1, t2) => Math.min(1, Math.max(-1, this.getDayOfMonthForTransaction(t2.transactions[0]) - this.getDayOfMonthForTransaction(t1.transactions[0]))))
                .map(dayGroup => <div className="TransactionList-DayEntry" key={`day-${dayGroup.key}`}>
                <div className="TransactionList-DayHeader">{dayGroup.key}</div>
                {dayGroup.transactions
                    .map((t : ExpenseDto | PaymentDto) =>
                    <div
                        className={
                            "TransactionList-TransactionEntry" +
                            (this.props.displayMode === DisplayMode.EXPENSE &&
                            (t as ExpenseDto).userSplits &&
                            Object.keys((t as ExpenseDto).userSplits!).length > 0
                                ? " TransactionList-TransactionEntry--CustomSplit"
                                : "")
                        }
                        key={`transaction-${t.id}`}
                    >
                        <div className="TransactionList-TransactionHeader">
                            <div className="TransactionList-AuthorIcon">
                                <UserDisplay user={this.getUserDtoForUser(t.authorUserId)} showName={false} smallIcon={false}/>
                            </div>
                            {this.props.displayMode === DisplayMode.EXPENSE && this.props.showDetails && (t as ExpenseDto).details ? (
                                <div className="TransactionList-TransactionDetailsText">{(t as ExpenseDto).details}</div>
                            ) : null}
                            {this.props.loggedInUserId === t.authorUserId && this.props.onDeleteTransaction && (
                                <button 
                                    className="TransactionList-DeleteBtn"
                                    onClick={() => this.props.onDeleteTransaction!(t.id)}
                                    title="Delete transaction"
                                    aria-label="Delete transaction"
                                >
                                    🗑️
                                </button>
                            )}
                        </div>
                        <div className="TransactionList-TransactionUsersRow">
                            <div className="TransactionList-TargetUsers">
                                {this.props.displayMode === DisplayMode.EXPENSE ? (
                                    <>
                                        {(t as ExpenseDto).targets.map(user => {
                                            const expense = t as ExpenseDto;
                                            const hasCustomSplit = expense.userSplits && Object.keys(expense.userSplits).length > 0;
                                            const userAmount = hasCustomSplit && expense.userSplits && expense.userSplits[user]
                                                ? expense.userSplits[user]
                                                : Math.floor(expense.amount / expense.targets.length);
                                            return (
                                                <div key={"user-" + user} className="TransactionList-UserWithAmount">
                                                    <UserDisplay user={this.getUserDtoForUser(user)} showName={false} smallIcon={true}/>
                                                    {hasCustomSplit && (
                                                        <span className="TransactionList-UserAmount">
                                                            €{(userAmount / 100).toFixed(2)}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <UserDisplay user={this.getUserDtoForUser((t as PaymentDto).userPaidTo)} showName={false} smallIcon={true}/>
                                )}
                            </div>
                            <div className="TransactionList-TransactionAmount">
                                <div>€&nbsp;{Math.floor(t.amount / 100)}.{t.amount % 100 > 9 ? "" : 0}{t.amount % 100}</div>
                            </div>
                        </div>
                    </div>)}
                </div>)}
        </div>
    }

    render() {
        return <div className="TransactionList-Wrapper">
            <div className="TransactionList-Header"><h1>{this.props.title}</h1></div>
            <div className="TransactionList-PaginationHeader">
                <Pagination page={this.state.page} pageHeaders={this.state.transactionsGrouped.map((t) => t.key)} showWhenSingle={true} onChange={(p: number) => {
                    this.setState({
                        page: p
                    })
                }}/>
            </div>
            {this.renderTransactions()}
        </div>
    }

}

export default TransactionList;